import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Users, Briefcase, MessageSquare, TrendingUp, CheckCircle, Zap, 
  Network, Sparkles, X, Star, Play, Shield, Clock, Globe, Award, Target,
  Rocket, Heart, Building2, UserCheck, Search, Calendar, BarChart3, FileText,
  ChevronRight, Quote, MapPin, DollarSign, Brain, Handshake, Bot, Check
} from 'lucide-react';
import ContactForm from '../components/ContactForm';

// SKO 2026 Promotion Configuration
const SKO_PROMO = {
  active: true,
  code: 'SKO2026',
  discount: 50,
  endDate: new Date('2026-02-28T23:59:59'),
  eventName: 'ServiceNow SKO 2026'
};

// Platform screenshots for demo
const PLATFORM_SCREENSHOTS = {
  matching: "https://customer-assets.emergentagent.com/job_brainymatch/artifacts/matching-screenshot.png",
  dashboard: "https://customer-assets.emergentagent.com/job_brainymatch/artifacts/dashboard-screenshot.png",
};

// Rotating headlines for hero section
const ROTATING_HEADLINES = [
  "50 Phone Calls",
  "100 Emails & WhatsApps",
  "Endless LinkedIn Hunting",
  "Days of Cold Outreach",
  "Manual Interview Scheduling"
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [showPromoBanner, setShowPromoBanner] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [activeFeature, setActiveFeature] = useState(0);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const isPromoActive = SKO_PROMO.active && new Date() < SKO_PROMO.endDate;

  useEffect(() => {
    if (!isPromoActive) return;
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = SKO_PROMO.endDate - now;
      
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [isPromoActive]);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 6);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate headlines
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setHeadlineIndex((prev) => (prev + 1) % ROTATING_HEADLINES.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const keyFeatures = [
    {
      icon: Brain,
      title: "AI-Powered Matching",
      description: "GPT-4 analyzes CVs, project descriptions, and compatibility signals to find perfect matches in seconds.",
      color: "purple",
      stat: "97%",
      statLabel: "Match Accuracy"
    },
    {
      icon: MapPin,
      title: "Geographic Intelligence",
      description: "Real-world distance calculation between consultants and projects. Never miss a local opportunity.",
      color: "blue",
      stat: "100+",
      statLabel: "Cities Mapped"
    },
    {
      icon: Calendar,
      title: "Interview Scheduling",
      description: "Google & Outlook calendar integration. Schedule interviews with one click, automatic reminders included.",
      color: "emerald",
      stat: "24h",
      statLabel: "Auto-Reminders"
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Real-time utilization rates, pipeline insights, and performance metrics for your entire team.",
      color: "orange",
      stat: "360°",
      statLabel: "Visibility"
    },
    {
      icon: MessageSquare,
      title: "Direct Messaging",
      description: "Company-to-company communication. No recruiters, no middlemen, no hidden fees.",
      color: "pink",
      stat: "0%",
      statLabel: "Commission"
    },
    {
      icon: FileText,
      title: "Contract Templates",
      description: "Pre-built legal templates for NDAs, service agreements, and more. Sign and collaborate faster.",
      color: "teal",
      stat: "5+",
      statLabel: "Templates"
    }
  ];

  const beforeAfter = [
    { before: "40+ phone calls", after: "One search query" },
    { before: "Weeks of sourcing", after: "Instant results" },
    { before: "100 emails sent", after: "Direct contact" },
    { before: "Excel spreadsheets", after: "Live dashboard" },
    { before: "Unknown candidates", after: "Verified profiles" },
    { before: "Manual scheduling", after: "Calendar sync" },
  ];

  const testimonials = [
    {
      quote: "Snow Exchange transformed how we staff our ServiceNow projects. What used to take weeks now takes hours.",
      author: "Sarah Chen",
      role: "Director of Delivery",
      company: "ServiceNow Elite Partner"
    },
    {
      quote: "For the first time, we're collaborating with other partners instead of competing. Everyone wins.",
      author: "Michael Roberts",
      role: "Managing Partner",
      company: "ITSM Consulting Group"
    },
    {
      quote: "The AI matching is incredible. It found compatibility factors we would never have considered.",
      author: "Emma Laurent",
      role: "Resource Manager",
      company: "Digital Transformation Co."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* SKO Promo Banner */}
      {isPromoActive && showPromoBanner && (
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-3 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-30"></div>
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 text-center relative">
            <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
            <span className="font-bold">
              🎰 <span className="hidden lg:inline">ServiceNow Sales KickOff 2026</span><span className="lg:hidden">{SKO_PROMO.eventName}</span> Special!
            </span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
              {SKO_PROMO.discount}% OFF
            </span>
            <span className="font-mono text-sm bg-black/20 px-2 py-1 rounded">
              {timeLeft.days}d {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
            </span>
            <span className="text-sm hidden sm:inline">Code: <code className="bg-white/20 px-2 rounded">{SKO_PROMO.code}</code></span>
            <button onClick={() => setShowPromoBanner(false)} className="absolute right-4 top-1/2 -translate-y-1/2 hover:bg-white/20 rounded p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" style={{ top: isPromoActive && showPromoBanner ? '44px' : '0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/snow-exchange-logo.png" alt="Snow Exchange" className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold" style={{ color: '#1B3240' }}>Snow Exchange</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })} className="hidden md:inline-flex text-gray-600">
                Features
              </Button>
              <Button variant="ghost" onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })} className="hidden md:inline-flex text-gray-600">
                How it Works
              </Button>
              <Button variant="ghost" onClick={() => navigate('/pricing')} className="hidden sm:inline-flex text-gray-600">
                Pricing
              </Button>
              <Button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B3240 0%, #0f1f2a 100%)' }}>
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-400 text-sm font-medium">The First ServiceNow Consultant Marketplace</span>
            </div>
            
            {/* Main headline - Fixed height container to prevent layout shift */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 leading-tight">
              {/* Fixed height container for rotating text */}
              <div className="flex flex-col items-center">
                <span className="block">Stop the</span>
                <div className="h-[3.5rem] sm:h-[3.5rem] lg:h-[5rem] flex items-center justify-center overflow-hidden">
                  <span 
                    className={`line-through text-gray-500 transition-opacity duration-300 text-3xl sm:text-4xl lg:text-5xl ${
                      isAnimating ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    {ROTATING_HEADLINES[headlineIndex]}
                  </span>
                </div>
              </div>
              <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Find Your Available Consultant in Seconds.
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Snow Exchange is revolutionizing how ServiceNow Partners collaborate. 
              <strong className="text-white"> Turn competitors into allies.</strong> Match consultants to projects instantly with AI. 
              <strong className="text-emerald-400"> Zero commissions. Direct connections.</strong>
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-8 py-6 text-lg font-bold shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all"
                onClick={() => navigate('/auth?mode=register')}
              >
                Start Free - No Credit Card
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="mr-2 w-5 h-5" />
                See How It Works
              </Button>
            </div>
            
            {/* Trust badges - better contrast on both dark and light backgrounds */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-sm max-w-2xl mx-auto relative z-20">
              <div className="flex items-center justify-center gap-2 bg-slate-800/90 backdrop-blur-sm rounded-lg py-2.5 px-3 border border-slate-600/50 text-white shadow-xl">
                <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="whitespace-nowrap font-medium">GDPR Compliant</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-slate-800/90 backdrop-blur-sm rounded-lg py-2.5 px-3 border border-slate-600/50 text-white shadow-xl">
                <Globe className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="whitespace-nowrap font-medium">Growing Network</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-slate-800/90 backdrop-blur-sm rounded-lg py-2.5 px-3 border border-slate-600/50 text-white shadow-xl">
                <Zap className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="whitespace-nowrap font-medium">AI-Powered</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-slate-800/90 backdrop-blur-sm rounded-lg py-2.5 px-3 border border-slate-600/50 text-white shadow-xl">
                <Heart className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="whitespace-nowrap font-medium">For ServiceNow</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave divider - positioned to overlap with section bottom */}
        <div className="absolute bottom-0 left-0 right-0 -mb-px">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* The Revolution Section */}
      <section className="py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              🚀 A New Era Begins
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1B3240' }}>
              The Old Way is <span className="line-through text-gray-400">Broken</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We built Snow Exchange because we lived the pain. Weeks wasted on sourcing. 
              Opportunities lost. Consultants sitting idle while projects suffered.
            </p>
          </div>
          
          {/* Before/After comparison */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Before */}
            <div className="bg-red-50 border border-red-100 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <X className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-700">The Old Way</h3>
                  <p className="text-red-600 text-sm">Time-consuming & frustrating</p>
                </div>
              </div>
              <ul className="space-y-4">
                {beforeAfter.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-red-700">
                    <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span>{item.before}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* After */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-emerald-700">With Snow Exchange</h3>
                  <p className="text-emerald-600 text-sm">Instant & effortless</p>
                </div>
              </div>
              <ul className="space-y-4">
                {beforeAfter.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-emerald-700">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>{item.after}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Visual Demo */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              ⚡ Simple & Powerful
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1B3240' }}>
              How Snow Exchange Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three steps to transform your resource management
            </p>
          </div>
          
          {/* Steps with screenshots */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-48 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <UserCheck className="w-12 h-12 text-white" />
                  </div>
                </div>
                <div className="absolute top-4 left-4 bg-white/20 backdrop-blur text-white text-sm font-bold px-3 py-1 rounded-full">
                  Step 1
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1B3240' }}>Create Your Profile</h3>
                <p className="text-gray-600 mb-4">
                  Sign up in 2 minutes. Add your consultants or post your project needs. 
                  Our AI analyzes skills, certifications, and availability automatically.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    CV auto-parsing
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Skill verification
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Certification tracking
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-48 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Search className="w-12 h-12 text-white" />
                  </div>
                </div>
                <div className="absolute top-4 left-4 bg-white/20 backdrop-blur text-white text-sm font-bold px-3 py-1 rounded-full">
                  Step 2
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1B3240' }}>AI Finds Your Match</h3>
                <p className="text-gray-600 mb-4">
                  Our GPT-4 powered matching analyzes skills, location, rate, and deep compatibility signals. 
                  Get ranked results with detailed insights in seconds.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    97% match accuracy
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Distance calculation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Budget compatibility
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-48 bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Handshake className="w-12 h-12 text-white" />
                  </div>
                </div>
                <div className="absolute top-4 left-4 bg-white/20 backdrop-blur text-white text-sm font-bold px-3 py-1 rounded-full">
                  Step 3
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1B3240' }}>Connect & Collaborate</h3>
                <p className="text-gray-600 mb-4">
                  Message directly, schedule interviews with calendar sync, and start collaborating. 
                  No middlemen. No commissions. Just results.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Direct messaging
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Calendar integration
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Contract templates
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Preview Section */}
      <section id="preview" className="py-20 px-4 sm:px-6 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-indigo-100 text-indigo-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              👀 Sneak Peek
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1B3240' }}>
              See What Awaits You
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A powerful, intuitive platform designed for ServiceNow professionals
            </p>
          </div>

          {/* Screenshot Carousel */}
          <div className="relative">
            {/* Main Preview Cards */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Consultant Marketplace Preview (Resources) - LEFT */}
              <div className="group relative bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-cyan-500"></div>
                <div className="p-4 bg-gray-50 border-b flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2">Marketplace</span>
                </div>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src="/screenshots/resources-preview.png?v=4" 
                    alt="Consultant Marketplace" 
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <div className="text-white">
                      <h4 className="font-bold text-xl mb-2 text-white" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>Rich Profiles</h4>
                      <p className="text-sm text-white/90" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>Skills, certifications, availability, and daily rates at a glance</p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg mb-2" style={{ color: '#1B3240' }}>Consultant Marketplace</h3>
                  <p className="text-gray-600 text-sm">Browse, filter, and discover top ServiceNow talent with detailed profiles.</p>
                </div>
              </div>

              {/* Interview Management Preview - CENTER (highlighted) */}
              <div className="group relative bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300 lg:-mt-4 lg:mb-4">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-indigo-600"></div>
                <div className="p-4 bg-gray-50 border-b flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2">Interviews</span>
                  <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">📅 Fully Integrated</span>
                </div>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src="/screenshots/interviews-preview.png?v=4" 
                    alt="Interview Management" 
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <div className="text-white">
                      <h4 className="font-bold text-xl mb-2 text-white" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>Schedule & Track Interviews</h4>
                      <p className="text-sm text-white/90" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>Google & Outlook integration, automatic reminders, one-click video calls</p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg mb-2" style={{ color: '#1B3240' }}>Interview Management</h3>
                  <p className="text-gray-600 text-sm">End-to-end interview scheduling with calendar sync and automated reminders.</p>
                </div>
              </div>

              {/* Collaboration Hub Preview - RIGHT */}
              <div className="group relative bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-500"></div>
                <div className="p-4 bg-gray-50 border-b flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-xs text-gray-500 ml-2">Collaborations</span>
                </div>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src="/screenshots/matching-preview.png?v=4" 
                    alt="Collaboration Management" 
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <div className="text-white">
                      <h4 className="font-bold text-xl mb-2 text-white" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>Track Every Engagement</h4>
                      <p className="text-sm text-white/90" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>Monitor progress, contracts, and consultant assignments in one place</p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg mb-2" style={{ color: '#1B3240' }}>Collaboration Hub</h3>
                  <p className="text-gray-600 text-sm">Manage active projects, pending approvals, and completed engagements.</p>
                </div>
              </div>
            </div>

            {/* CTA Below Screenshots */}
            <div className="mt-12 text-center">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                Start Exploring Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="mt-4 text-gray-500 text-sm">Free account • No credit card required</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Augmented Future Section */}
      <section id="ai-future" className="py-20 px-4 sm:px-6 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-20 w-64 h-64 bg-purple-500 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-indigo-500 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '0.75s' }}></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-purple-500/20 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-400 text-sm font-medium">The Future is Here</span>
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              Ready for{' '}
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-emerald-400 bg-clip-text text-transparent">
                AI-Augmented Consultants
              </span>
            </h2>
            
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              The ServiceNow ecosystem is evolving. Consultants equipped with AI agents are delivering 
              <strong className="text-white"> more value in less time</strong>—happier clients, shorter missions, better results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* AI-Augmented Consultants Card */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">AI-Augmented Consultants</h3>
                  <span className="text-purple-400 text-sm font-medium">Enhanced Productivity</span>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                Consultants who leverage their own AI agents to <strong className="text-white">multiply their productivity</strong> on implementations, 
                advisory missions, and general consulting work. They maximize the <em>results-to-time ratio</em>—clients get more value at similar cost with faster delivery.
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </span>
                  <span>Distinctive badge on marketplace profiles</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </span>
                  <span>AI tools listed in resource profiles</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </span>
                  <span>PM filter for AI-augmented preference</span>
                </li>
              </ul>
            </div>

            {/* AI Agents as Consultants Card */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <Bot className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">AI Agents as Consultants</h3>
                  <span className="text-emerald-400 text-sm font-medium">Coming Soon</span>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                We're preparing to welcome the first <strong className="text-white">AI Agents as standalone consultants</strong> on our marketplace. 
                Developers building ServiceNow-focused AI agents can soon offer them directly to Partners and end clients—managed by humans or operating autonomously.
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3 h-3 text-purple-400" />
                  </span>
                  <span>Dedicated agent profiles section</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3 h-3 text-purple-400" />
                  </span>
                  <span>Usage-based or subscription pricing</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3 h-3 text-purple-400" />
                  </span>
                  <span>Human AI-manager or autonomous modes</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA for AI Agent Developers */}
          <div className="text-center bg-gradient-to-r from-purple-500/10 to-emerald-500/10 border border-purple-500/20 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-3">Building AI Agents for ServiceNow?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              We're looking for early pioneers to feature on our marketplace. Get in touch to discuss how we can showcase your AI agents to ServiceNow Partners worldwide.
            </p>
            <Button 
              variant="outline"
              className="border-purple-400/50 text-purple-300 hover:bg-purple-500/20 hover:text-white px-6 py-3"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              <MessageSquare className="mr-2 w-4 h-4" />
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              ✨ Powerful Features
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1B3240' }}>
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built by ServiceNow experts, for ServiceNow professionals
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {keyFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              const colorClasses = {
                purple: 'bg-purple-100 text-purple-600 border-purple-200',
                blue: 'bg-blue-100 text-blue-600 border-blue-200',
                emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
                orange: 'bg-orange-100 text-orange-600 border-orange-200',
                pink: 'bg-pink-100 text-pink-600 border-pink-200',
                teal: 'bg-teal-100 text-teal-600 border-teal-200',
              };
              
              return (
                <div 
                  key={idx}
                  className={`bg-white rounded-2xl p-6 border-2 transition-all cursor-pointer hover:shadow-lg ${activeFeature === idx ? 'border-emerald-400 shadow-lg' : 'border-gray-100'}`}
                  onClick={() => setActiveFeature(idx)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[feature.color]}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color: '#1B3240' }}>{feature.stat}</div>
                      <div className="text-xs text-gray-500">{feature.statLabel}</div>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: '#1B3240' }}>{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Mission - Collaboration over Competition */}
      <section className="py-20 px-4 sm:px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B3240 0%, #2D4A5E 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-emerald-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-8">
            <Heart className="w-4 h-4 text-red-400" />
            <span className="text-white/90 text-sm font-medium">Our Mission</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
            Turning Competitors into <span className="text-emerald-400">Collaborators</span>
          </h2>
          
          <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            In the ServiceNow ecosystem, we believe the collective interest outweighs individual rivalries. 
            When partners work together, <strong className="text-white">everyone wins</strong> — consultants find work, 
            projects get staffed, and ServiceNow adoption grows. 
            <span className="block mt-4 text-emerald-400 font-semibold">
              This is not just a marketplace. It is a movement.
            </span>
          </p>
          
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-10">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="text-4xl font-bold text-emerald-400 mb-2">€0</div>
              <div className="text-white/80 text-sm">Commission on Placements</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="text-4xl font-bold text-emerald-400 mb-2">100%</div>
              <div className="text-white/80 text-sm">Direct Partner Connections</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="text-4xl font-bold text-emerald-400 mb-2">∞</div>
              <div className="text-white/80 text-sm">Collaboration Potential</div>
            </div>
          </div>
          
          <Button 
            size="lg" 
            className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 text-lg font-bold"
            onClick={() => navigate('/auth?mode=register')}
          >
            Join the Movement
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Personas Section */}
      <section className="py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              👥 Built For Everyone
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#1B3240' }}>
              Your Role, Your Solution
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Resource Managers */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-blue-500 hover:shadow-xl transition-all group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-blue-100 group-hover:scale-110 transition-transform">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1B3240' }}>Resource Managers</h3>
              <p className="text-gray-600 mb-6">
                Manage consultant portfolios, maximize utilization, and find the perfect projects for your team.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Unlimited consultant profiles</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AI project matching</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Team analytics dashboard</span>
                </li>
              </ul>
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/auth?mode=register&role=rm')}>
                Get Started as RM
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Project Managers */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-emerald-500 hover:shadow-xl transition-all group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-emerald-100 group-hover:scale-110 transition-transform">
                <Target className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1B3240' }}>Project Managers</h3>
              <p className="text-gray-600 mb-6">
                Find qualified ServiceNow consultants in seconds. Staff projects with confidence, not guesswork.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Post unlimited projects</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Instant compatibility scores</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Integrated interview scheduling</span>
                </li>
              </ul>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/auth?mode=register&role=pm')}>
                Get Started as PM
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Freelancers */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-purple-500 hover:shadow-xl transition-all group">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-purple-100 group-hover:scale-110 transition-transform">
                <Rocket className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1B3240' }}>Freelancers</h3>
              <p className="text-gray-600 mb-6">
                Showcase your ServiceNow expertise and get discovered by top companies worldwide.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Professional public profile</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AI opportunity matching</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Optional representation</span>
                </li>
              </ul>
              <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => navigate('/auth?mode=register&role=freelancer')}>
                Get Started as Freelancer
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - HIDDEN until we have real users
      <section className="py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-yellow-100 text-yellow-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              ⭐ Trusted by Partners
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#1B3240' }}>
              What Our Users Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-8 relative">
                <Quote className="w-10 h-10 text-emerald-200 absolute top-6 right-6" />
                <p className="text-gray-700 mb-6 italic relative z-10">&ldquo;{testimonial.quote}&rdquo;</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold">
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-bold" style={{ color: '#1B3240' }}>{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                    <div className="text-xs text-emerald-600">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 bg-gradient-to-r from-emerald-500 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Resource Management?
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join the ServiceNow partner community that is redefining collaboration. 
            Start for free, no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-100 px-10 py-6 text-lg font-bold shadow-xl"
              onClick={() => navigate('/auth?mode=register')}
            >
              Create Your Free Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white/20 px-8 py-6 text-lg"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Talk to Us
            </Button>
          </div>
          
          <p className="text-white/70 text-sm mt-8">
            🎰 Use code <strong className="text-white">SKO2026</strong> for 50% off your first year!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-gray-100">
        <ContactForm />
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <img src="/snow-exchange-logo-dark-bg.png" alt="Snow Exchange" className="h-10 w-auto" />
                <span className="text-lg font-bold">SnowExchange</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                The modern infrastructure for ServiceNow staffing and delivery.
              </p>
            </div>

            {/* SnowExchange Column */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">SnowExchange</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => navigate('/vision')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Vision
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/careers')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Careers
                  </button>
                </li>
                <li>
                  <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => navigate('/legal-notice')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Legal Notice
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Terms of Use
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/ai-disclaimer')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    AI Disclaimer
                  </button>
                </li>
              </ul>
            </div>

            {/* Product Column */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Product</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => navigate('/pricing')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Pricing
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-500 text-sm text-center">
              © 2026 SnowExchange. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
